/**
 * Property upload keys and static paths (shared frontend build + runtime).
 */

const PROPERTY_KEY_PREFIX = 'property/';

/**
 * Normalize a stored image ref to a `property/…` upload key, or null.
 *
 * @param {unknown} ref
 * @returns {string|null}
 */
export function extractPropertyImageKey(ref) {
  if (ref == null || ref === '') return null;
  const t = String(ref).trim();
  if (!t) return null;
  if (t.startsWith('/images/property/')) {
    return t.slice('/images/'.length);
  }
  if (t.startsWith(PROPERTY_KEY_PREFIX)) {
    return t.replace(/^property\/property\//, PROPERTY_KEY_PREFIX);
  }
  const uploadMatch = t.match(/\/v1\/uploads\/(property\/.+)$/i);
  if (uploadMatch) {
    return uploadMatch[1].replace(/^property\/property\//, PROPERTY_KEY_PREFIX);
  }
  return null;
}

/**
 * Site-root URL for a bundled property upload (`dist/images/property/…`).
 *
 * @param {unknown} ref
 * @returns {string|null}
 */
export function staticPathForPropertyKey(ref) {
  const key = extractPropertyImageKey(ref);
  if (!key) return null;
  return `/images/${key}`;
}

/**
 * Whether a property image ref should be served from `/v1/uploads/…` (live store)
 * instead of the static `/images/property/…` bundle path.
 *
 * @param {unknown} ref
 * @param {{ useUploadApi?: boolean, dev?: boolean }} [opts]
 */
export function shouldResolvePropertyKeyViaUploadApi(ref, opts = {}) {
  const propertyKey = extractPropertyImageKey(ref);
  if (!propertyKey) return false;
  if (opts.useUploadApi || opts.dev) return true;
  return String(ref).includes('/v1/uploads/');
}

/**
 * Same-origin upload path for a property image key (no locale — images are shared).
 *
 * @param {unknown} ref
 * @returns {string}
 */
export function uploadApiPathForPropertyKey(ref) {
  const key = extractPropertyImageKey(ref);
  if (!key) return '';
  return `/v1/uploads/${key}`;
}

/**
 * Collect floor-plan and room image keys from composed floor wire data.
 *
 * @param {object[]|null|undefined} floors
 * @returns {string[]}
 */
export function collectFloorPlanImageKeys(floors) {
  const keys = new Set();
  const add = (ref) => {
    const key = extractPropertyImageKey(ref);
    if (key) keys.add(key);
  };

  for (const floor of Array.isArray(floors) ? floors : []) {
    add(floor?.planImage);
    for (const room of Array.isArray(floor?.rooms) ? floor.rooms : []) {
      for (const image of Array.isArray(room?.images) ? room.images : []) {
        add(image?.src);
      }
    }
  }

  return [...keys];
}

/**
 * Rewrite floor plan + room image refs to static `/images/property/…` paths.
 *
 * @param {object[]|null|undefined} floors
 * @returns {object[]}
 */
/**
 * Collect hero + gallery image keys from composed property wire data.
 *
 * @param {object|null|undefined} property
 * @returns {string[]}
 */
/**
 * Room id embedded in a room-scoped upload key (`property/rooms/<id>/…`), or null.
 *
 * @param {unknown} ref
 * @returns {string|null}
 */
export function extractRoomIdFromPropertyImageKey(ref) {
  const key = extractPropertyImageKey(ref);
  if (!key) return null;
  const match = key.match(/^property\/rooms\/([a-f0-9]{24})\//i);
  return match ? match[1] : null;
}

/**
 * Gallery (or other) refs that belong to a specific room upload folder.
 *
 * @param {unknown} roomId
 * @param {Array<{ src?: unknown }>|null|undefined} gallery
 * @returns {string[]}
 */
export function collectRoomScopedGalleryImageKeys(roomId, gallery) {
  const id = String(roomId || '');
  if (!id) return [];
  const keys = [];
  for (const item of Array.isArray(gallery) ? gallery : []) {
    const key = extractPropertyImageKey(item?.src);
    if (key && extractRoomIdFromPropertyImageKey(key) === id) keys.push(key);
  }
  return keys;
}

/**
 * Merge canonical room `images[]` with extra src refs scoped to the same room.
 * Preserves existing order; appends new keys once.
 *
 * @param {unknown} roomId
 * @param {Array<{ src?: unknown }>|null|undefined} images
 * @param {unknown[]} extraSrcRefs
 * @returns {Array<{ src: string }>}
 */
export function mergeRoomCanonicalImageRefs(roomId, images, extraSrcRefs = []) {
  const id = String(roomId || '');
  const merged = [];
  const seen = new Set();

  for (const image of Array.isArray(images) ? images : []) {
    const key = extractPropertyImageKey(image?.src);
    const src = key || String(image?.src || '').trim();
    if (!src || seen.has(src)) continue;
    const scopedId = key ? extractRoomIdFromPropertyImageKey(key) : null;
    if (id && scopedId && scopedId !== id) continue;
    seen.add(src);
    merged.push({ src });
  }

  for (const ref of extraSrcRefs) {
    const key = extractPropertyImageKey(ref);
    if (!key || seen.has(key)) continue;
    if (id && extractRoomIdFromPropertyImageKey(key) !== id) continue;
    seen.add(key);
    merged.push({ src: key });
  }

  return merged;
}

/**
 * Keep editor photos when they already include the server list plus newer uploads.
 * Avoids a stale graph reload wiping a photo that was just uploaded.
 *
 * @param {Array<{ src?: unknown }>|null|undefined} editorImages
 * @param {Array<{ src?: unknown }>|null|undefined} serverImages
 * @returns {Array<{ src: string }>}
 */
export function preferEditorRoomImagesIfAhead(editorImages, serverImages) {
  const editor = mergeRoomCanonicalImageRefs('', editorImages, []);
  const server = mergeRoomCanonicalImageRefs('', serverImages, []);
  const editorSrc = new Set(editor.map((item) => item.src));
  const serverSrc = new Set(server.map((item) => item.src));
  const editorHasAllServer = server.every((item) => editorSrc.has(item.src));
  const editorHasExtra = editor.some((item) => !serverSrc.has(item.src));
  if (editor.length && editorHasAllServer && editorHasExtra) return editor;
  return server;
}

export function collectPropertyMarketingImageKeys(property) {
  const keys = new Set();
  const add = (ref) => {
    const key = extractPropertyImageKey(ref);
    if (key) keys.add(key);
  };

  if (!property || typeof property !== 'object') return [];

  add(property.heroImage);
  for (const item of Array.isArray(property.gallery) ? property.gallery : []) {
    add(item?.src);
  }

  const marketing = property.marketing;
  if (marketing && typeof marketing === 'object') {
    add(marketing.heroImage);
    for (const item of Array.isArray(marketing.gallery) ? marketing.gallery : []) {
      add(item?.src);
    }
  }

  return [...keys];
}

/**
 * Rewrite hero + gallery refs on property wire data to static `/images/property/…` paths.
 *
 * @param {object|null|undefined} property
 * @returns {object|null|undefined}
 */
export function rewritePropertyMarketingImageRefsForStatic(property) {
  if (!property || typeof property !== 'object') return property;

  const rewriteGallery = (items) => {
    if (!Array.isArray(items)) return items;
    return items.map((image) => {
      if (!image || typeof image !== 'object') return image;
      const srcStatic = staticPathForPropertyKey(image.src);
      return srcStatic ? { ...image, src: srcStatic } : image;
    });
  };

  const out = { ...property };

  const heroStatic = staticPathForPropertyKey(out.heroImage);
  if (heroStatic) out.heroImage = heroStatic;
  if (Array.isArray(out.gallery)) out.gallery = rewriteGallery(out.gallery);

  if (out.marketing && typeof out.marketing === 'object') {
    const marketing = { ...out.marketing };
    const marketingHeroStatic = staticPathForPropertyKey(marketing.heroImage);
    if (marketingHeroStatic) marketing.heroImage = marketingHeroStatic;
    if (Array.isArray(marketing.gallery)) marketing.gallery = rewriteGallery(marketing.gallery);
    out.marketing = marketing;
  }

  return out;
}

export function rewriteFloorsImageRefsForStatic(floors) {
  if (!Array.isArray(floors)) return [];

  return floors.map((floor) => {
    const out = { ...floor };
    const planStatic = staticPathForPropertyKey(out.planImage);
    if (planStatic) out.planImage = planStatic;

    if (Array.isArray(out.rooms)) {
      out.rooms = out.rooms.map((room) => {
        const r = { ...room };
        if (!Array.isArray(r.images)) return r;
        r.images = r.images.map((image) => {
          if (!image || typeof image !== 'object') return image;
          const srcStatic = staticPathForPropertyKey(image.src);
          return srcStatic ? { ...image, src: srcStatic } : image;
        });
        return r;
      });
    }

    return out;
  });
}
